/* =============================================================================
   VIEWS / ADMIN / RESERVATIONS-HELPERS — Geteilte UI-Helpers + State-Accessor
   =============================================================================
   S69 REF-3 Cut 1/5: aus reservations.js (1.429 LOC) extrahiert.

   Enthält:
     - getState()                — zentraler Storage-State-Accessor
     - tableNamesFor(ids)        — Komma-Liste der Tisch-Namen
     - areaLabel(a)              — Bereich-Label (indoor/outdoor/club/any)
     - statusBadge(r)            — Status-Badge HTML
     - renderReservationItem(r)  — Listen-Item HTML (auch vom Dashboard genutzt)

   Diese Helpers werden von ALLEN reservations-Sub-Modulen importiert:
   helpers ist der gemeinsame Boden. Memory `gg-module-extract` — private
   Helpers explizit exportieren.
============================================================================ */

import {
  htmlEscape, todayISO, dayNum, monthShort,
  normalizePreorderItems
} from '../../utils/helpers.js';
// S239 Cut A0 — „✅ Gäste sind da" in der Reservierungs-Zeile.
import { findCheckinOccupancy, reservationCheckinButton, reservationNoShowButton } from './reservation-checkin.js';

export function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], tables: [] };
}

export function tableNamesFor(ids) {
  const state = getState();
  return (ids || []).map(id => {
    const t = state.tables.find(x => x.id === id);
    return t ? t.name : '?';
  }).join(', ');
}

export function areaLabel(a) {
  return { indoor: 'Innenbereich', outdoor: 'Außenbereich', club: 'Clubraum', any: 'flexibel' }[a] || a;
}

export function statusBadge(r) {
  if (r.status === 'pending')   return '<span class="badge badge-pending">offen</span>';
  if (r.status === 'confirmed') return '<span class="badge badge-confirmed">bestätigt</span>';
  if (r.status === 'declined')  return '<span class="badge badge-declined">abgesagt</span>';
  if (r.status === 'cancelled') return '<span class="badge badge-declined">Kunden-Storno</span>';
  if (r.status === 'no-show')   return '<span class="badge badge-declined">No-Show</span>';
  if (r.status === 'waitlist')  return '<span class="badge badge-manual">Warteliste</span>';
  if (r.status === 'completed') return '<span class="badge badge-completed">erledigt</span>';
  return '';
}

export function renderReservationItem(r) {
  const archived = r.date < todayISO() && r.status === 'confirmed';
  const itemClass = archived ? 'completed' : r.status;
  return `
    <div class="res-item ${itemClass}">
      <div class="res-date">
        <div class="day">${dayNum(r.date)}</div>
        <div class="month">${monthShort(r.date)}</div>
        <div class="time">${r.time}</div>
      </div>
      <div class="res-main">
        <div class="name">${htmlEscape(r.name)} · ${r.guests} Personen</div>
        <div class="meta">
          ${htmlEscape(r.phone)} · ${htmlEscape(r.email)} ·
          ${r.assignedTables && r.assignedTables.length
            ? (() => { const uc = Number(r.extraChairsUsed) || (r.needsExtraChair ? 1 : 0); return `Tisch${r.assignedTables.length>1?'e':''}: ${htmlEscape(tableNamesFor(r.assignedTables))}${r.tablesCombined ? ' (kombiniert)':''}${uc > 0 ? ` + ${uc} Stuhl${uc > 1 ? 'e' : ''}` : ''}`; })()
            : `Bereich: ${areaLabel(r.area)}`}
        </div>
        <div class="meta-tags">
          ${statusBadge(r)}
          ${r.wishTableId ? `<span class="badge badge-wish" title="Unverbindlicher Gast-Wunschtisch — Zuweisung frei beim Annehmen">💺 Wunsch: ${htmlEscape(tableNamesFor([r.wishTableId]))}</span>` : ''}
          ${r.serviceType === 'takeaway' ? '<span class="badge badge-takeaway">🥡 Abholung</span>' : ''}
          ${r.requiresManualConfirm && r.status === 'pending' ? '<span class="badge badge-manual">manuelle Prüfung</span>' : ''}
          ${(() => {
            const items = normalizePreorderItems(r.preorder);
            return items.length
              ? `<span class="badge badge-completed">${items.reduce((s, i) => s + i.qty, 0)} Vorbestellungen</span>`
              : '';
          })()}
        </div>
        ${r.notes ? `<div class="notes">📝 ${htmlEscape(r.notes)}</div>` : ''}
        ${r.declineHint ? `<div class="notes" style="background:var(--danger-soft); color:var(--danger);">⚠️ ${htmlEscape(r.declineHint)}</div>` : ''}
      </div>
      <div class="res-actions">
        ${r.status === 'pending' ? `
          <button class="btn btn-primary btn-sm" onclick="confirmReservation('${r.id}')">Bestätigen</button>
          <button class="btn btn-danger btn-sm" onclick="declineReservation('${r.id}')">Absagen</button>
        ` : ''}
        ${r.status === 'confirmed' ? `
          ${(() => {
            // S239 Cut A0 — „✅ Gäste sind da" direkt in der Zeile: nur für HEUTE
            // + bestätigt + Tisch zugewiesen; nach dem Check-in führt der Platz
            // zur Belegung (dort hängen Bestellen/Kassieren/Freigeben).
            const occ = findCheckinOccupancy(r.id, (getState().tableOccupancies || []));
            const btn = reservationCheckinButton(r, occ);
            if (!btn.show) return '';
            return btn.mode === 'checkin'
              ? `<button class="btn btn-primary btn-sm" onclick="checkinReservation('${htmlEscape(r.id)}')" title="Gäste sind eingetroffen — Tisch wird belegt, ab hier wie ein Walk-In">${btn.label}</button>`
              : `<button class="btn btn-secondary btn-sm" onclick="openOccupancyWalkInModal('${htmlEscape(btn.occupancyId)}')" title="Belegung dieser Reservierung öffnen">${btn.label}</button>`;
          })()}
          <button class="btn btn-secondary btn-sm" onclick="sendConfirmationEmail('${r.id}')">E-Mail</button>
          ${(() => {
            // S240-HF6: dieselbe Sichtbarkeits-Regel wie im Detail-Modal
            // (No-Show nur heute/vergangen) — EIN Helfer, keine zweite Logik.
            const ns = reservationNoShowButton(r, {
              featureEnabled: !!(window.AppConfig && window.AppConfig.features
                && window.AppConfig.features.noShowTracking)
            });
            return (ns.show && ns.mode === 'noshow')
              ? `<button class="btn btn-ghost btn-sm" onclick="markAsNoShow('${htmlEscape(r.id)}')" title="Als No-Show markieren">${ns.label}</button>`
              : '';
          })()}
        ` : ''}
        ${r.status === 'no-show' ? `
          <button class="btn btn-ghost btn-sm" onclick="revertNoShow('${r.id}')">No-Show aufheben</button>
        ` : ''}
        <button class="btn btn-ghost btn-sm" onclick="openReservationDetail('${r.id}')">Details</button>
      </div>
    </div>
  `;
}

// window-Bindings für inline-onclick-Konsumenten (das Hauptmodul re-bindet
// zusätzlich, das hier sichert Side-Effect bei direktem Modul-Import).
if (typeof window !== 'undefined') {
  Object.assign(window, {
    tableNamesFor, areaLabel, statusBadge, renderReservationItem
  });
}
